import api from './api';

// Types
export interface DataSource {
  id: string;
  name: string;
  description: string;
  source_type: string;
  configuration: Record<string, any>;
  status: string;
  last_sync_at?: string;
  created_at: string;
  updated_at: string;
}

export interface KPI {
  id: string;
  name: string;
  description: string;
  category: string;
  unit: string;
  frequency: string;
  functional_hierarchy_name?: string;
  kpi_type: string; // Changed from is_universal to match backend response
  status: string; // Added to match backend response
  master_name?: string; // Added to match backend response
  industries?: string[]; // Added for industry filtering
  created_at: string;
  updated_at: string;
}

export interface KPIMapping {
  id: string;
  tenant_id: string;
  kpi_id: string;
  data_source_id: string;
  field_mappings: Record<string, string>;
  calculation_frequency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConnectorType {
  type: string;
  name: string;
  description: string;
  configSchema: Record<string, any>;
}

export interface ConnectorInstance {
  id: string;
  tenant_id: string;
  connector_type: string;
  name: string;
  description: string;
  configuration: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ImportedData {
  id: string;
  tenant_id: string;
  data_source_id: string;
  data: Record<string, any>[];
  import_status: string;
  import_date: string;
  row_count: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

// Data Source Management
export const dataConnectionService = {
  // Get all data sources for tenant
  async getDataSources(): Promise<DataSource[]> {
    const response = await api.get('/data-connection/sources');
    return response.data.data;
  },

  // Create new data source
  async createDataSource(data: Partial<DataSource>): Promise<DataSource> {
    const response = await api.post('/data-connection/sources', data);
    return response.data.data;
  },

  // Update data source
  async updateDataSource(id: string, data: Partial<DataSource>): Promise<DataSource> {
    const response = await api.put(`/data-connection/sources/${id}`, data);
    return response.data.data;
  },

  // Delete data source
  async deleteDataSource(id: string): Promise<void> {
    await api.delete(`/data-connection/sources/${id}`);
  },

  // Get data source by ID
  async getDataSource(id: string): Promise<DataSource> {
    const response = await api.get(`/data-connection/sources/${id}`);
    return response.data.data;
  }
};

// KPI Management
export const kpiService = {
  // Get out-of-box KPIs
  async getOutOfBoxKPIs(industry?: string): Promise<KPI[]> {
    const params: any = { outOfBox: true };
    if (industry && industry !== 'all') {
      params.industry = industry;
    }
    const response = await api.get('/kpis', { params });
    return response.data.data;
  },

  // Get tenant-specific KPIs
  async getTenantKPIs(): Promise<KPI[]> {
    const response = await api.get('/data-connection/kpis/tenant');
    return response.data.data;
  },

  // Get business functions
  async getBusinessFunctions(): Promise<any[]> {
    const response = await api.get('/data-connection/kpis/business-functions');
    return response.data.data;
  },

  // Adopt KPI from out-of-box library
  async adoptKPI(kpiId: string, customizations?: Partial<KPI>): Promise<KPI> {
    const response = await api.post(`/data-connection/kpis/${kpiId}/adopt`, customizations);
    return response.data.data;
  }
};

// KPI Mapping Management
export const kpiMappingService = {
  // Get KPI mappings
  async getKPIMappings(kpiId?: string): Promise<KPIMapping[]> {
    const params = kpiId ? { kpiId } : {};
    const response = await api.get('/data-connection/kpi-mappings', { params });
    return response.data.data;
  },

  // Create KPI mapping
  async createKPIMapping(data: Partial<KPIMapping>): Promise<KPIMapping> {
    const response = await api.post('/data-connection/kpi-mappings', data);
    return response.data.data;
  },

  // Update KPI mapping
  async updateKPIMapping(id: string, data: Partial<KPIMapping>): Promise<KPIMapping> {
    const response = await api.put(`/data-connection/kpi-mappings/${id}`, data);
    return response.data.data;
  },

  // Delete KPI mapping
  async deleteKPIMapping(id: string): Promise<void> {
    await api.delete(`/data-connection/kpi-mappings/${id}`);
  }
};

// Connector Management
export const connectorService = {
  // Get available connector types
  async getConnectorTypes(): Promise<ConnectorType[]> {
    const response = await api.get('/data-connection/connectors');
    return response.data.data;
  },

  // Get connector instances
  async getConnectorInstances(): Promise<ConnectorInstance[]> {
    const response = await api.get('/data-connection/connectors/instances');
    return response.data.data;
  },

  // Create connector instance
  async createConnectorInstance(data: Partial<ConnectorInstance>): Promise<ConnectorInstance> {
    const response = await api.post('/data-connection/connectors/instances', data);
    return response.data.data;
  },

  // Test connector connection
  async testConnector(connectorType: string, config: Record<string, any>): Promise<any> {
    const response = await api.post('/data-connection/connectors/test', {
      connectorType,
      config
    });
    return response.data.data;
  },

  // Discover schema from data source
  async discoverSchema(connectorType: string, config: Record<string, any>): Promise<any> {
    const response = await api.post('/data-connection/connectors/discover', {
      connectorType,
      config
    });
    return response.data.data;
  }
};

// Data Import Management
export const dataImportService = {
  // Get imported data
  async getImportedData(): Promise<ImportedData[]> {
    const response = await api.get('/data-connection/imports');
    return response.data.data;
  },

  // Import data
  async importData(data: Partial<ImportedData>): Promise<ImportedData> {
    const response = await api.post('/data-connection/imports', data);
    return response.data.data;
  }
};

export default {
  dataConnectionService,
  kpiService,
  kpiMappingService,
  connectorService,
  dataImportService
};
